import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, Card, CardContent, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Replay as RecurringIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecurringCharges() {
  const [charges, setCharges] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [upcoming, setUpcoming] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    day_of_charge: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    auto_generate_receivable: true,
    send_whatsapp_reminder: false,
    reminder_days_before: 3
  });

  useEffect(() => {
    fetchCharges();
    fetchCustomers();
    fetchUpcoming();
  }, []);

  const fetchCharges = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/recurring`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharges(response.data.data || []);
    } catch (error) {
      toast.error('Erro ao carregar cobranças');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes');
    }
  };

  const fetchUpcoming = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/recurring/upcoming?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcoming(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar próximas cobranças');
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        day_of_charge: parseInt(formData.day_of_charge)
      };

      if (editingCharge) {
        await axios.put(`${API}/api/recurring/${editingCharge.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cobrança atualizada!');
      } else {
        await axios.post(`${API}/api/recurring`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cobrança criada!');
      }

      setOpenDialog(false);
      resetForm();
      fetchCharges();
      fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  };

  const handleEdit = (charge) => {
    setEditingCharge(charge);
    setFormData({
      customer_id: charge.customer_id,
      description: charge.description,
      amount: charge.amount,
      frequency: charge.frequency,
      day_of_charge: charge.day_of_charge,
      start_date: charge.start_date,
      end_date: charge.end_date || '',
      auto_generate_receivable: charge.auto_generate_receivable,
      send_whatsapp_reminder: charge.send_whatsapp_reminder,
      reminder_days_before: charge.reminder_days_before
    });
    setOpenDialog(true);
  };

  const handleDelete = async (charge) => {
    if (!window.confirm('Excluir esta cobrança recorrente?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/recurring/${charge.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cobrança excluída');
      fetchCharges();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleToggleActive = async (charge) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/recurring/${charge.id}`, 
        { is_active: !charge.is_active },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(charge.is_active ? 'Cobrança pausada' : 'Cobrança ativada');
      fetchCharges();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const resetForm = () => {
    setEditingCharge(null);
    setFormData({
      customer_id: '',
      description: '',
      amount: '',
      frequency: 'monthly',
      day_of_charge: 1,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      auto_generate_receivable: true,
      send_whatsapp_reminder: false,
      reminder_days_before: 3
    });
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      weekly: 'Semanal',
      biweekly: 'Quinzenal',
      monthly: 'Mensal',
      quarterly: 'Trimestral',
      yearly: 'Anual'
    };
    return labels[freq] || freq;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <RecurringIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cobranças Recorrentes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { resetForm(); setOpenDialog(true); }}
        >
          Nova Cobrança
        </Button>
      </Box>

      {/* Cards de próximas cobranças */}
      {upcoming.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Próximas Cobranças (7 dias)</Typography>
          <Grid container spacing={2}>
            {upcoming.slice(0, 4).map((charge) => (
              <Grid item xs={12} md={3} key={charge.id}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      {new Date(charge.next_charge_date).toLocaleDateString('pt-BR')}
                    </Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(charge.amount)}
                    </Typography>
                    <Typography variant="body2">{charge.customer?.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {charge.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Frequência</TableCell>
              <TableCell>Próxima Cobrança</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : charges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhuma cobrança recorrente cadastrada
                </TableCell>
              </TableRow>
            ) : (
              charges.map((charge) => (
                <TableRow key={charge.id}>
                  <TableCell>{charge.customer?.name}</TableCell>
                  <TableCell>
                    {charge.description}
                    {charge.send_whatsapp_reminder && (
                      <WhatsAppIcon fontSize="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(charge.amount)}
                  </TableCell>
                  <TableCell>
                    <Chip label={getFrequencyLabel(charge.frequency)} size="small" />
                  </TableCell>
                  <TableCell>
                    {new Date(charge.next_charge_date).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={charge.is_active ? 'Ativa' : 'Pausada'}
                      color={charge.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(charge)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(charge)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(charge)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Criar/Editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCharge ? 'Editar Cobrança' : 'Nova Cobrança Recorrente'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={customers}
                value={customers.find((c) => String(c.id) === String(formData.customer_id)) || null}
                onChange={(_event, value) => {
                  setFormData({ ...formData, customer_id: value?.id || '' });
                }}
                getOptionLabel={(option) =>
                  `${option?.name || ''}${option?.document ? ` - ${option.document}` : ''}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label="Cliente"
                    placeholder="Digite para buscar cliente"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Descrição"
                size="small"
                sx={{ width: '100%' }}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor"
                type="number"
                size="small"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequência</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequência"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                >
                  <MenuItem value="weekly">Semanal</MenuItem>
                  <MenuItem value="biweekly">Quinzenal</MenuItem>
                  <MenuItem value="monthly">Mensal</MenuItem>
                  <MenuItem value="quarterly">Trimestral</MenuItem>
                  <MenuItem value="yearly">Anual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Dia da Cobrança"
                type="number"
                size="small"
                inputProps={{ min: 1, max: 31 }}
                value={formData.day_of_charge}
                onChange={(e) => setFormData({ ...formData, day_of_charge: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Data Início"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Data Fim (opcional)"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.auto_generate_receivable}
                          onChange={(e) => setFormData({ ...formData, auto_generate_receivable: e.target.checked })}
                        />
                      }
                      label="Gerar conta a receber automaticamente"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.send_whatsapp_reminder}
                          onChange={(e) => setFormData({ ...formData, send_whatsapp_reminder: e.target.checked })}
                        />
                      }
                      label="Enviar lembrete por WhatsApp"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {formData.send_whatsapp_reminder && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dias antes para lembrete"
                  type="number"
                  size="small"
                  value={formData.reminder_days_before}
                  onChange={(e) => setFormData({ ...formData, reminder_days_before: e.target.value })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingCharge ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
